import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getConsolidatedPerson,
  ConsolidatedPerson,
} from '../services/investigationApi';

function InvestigationPersonDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ConsolidatedPerson | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getConsolidatedPerson(id)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 text-center text-gray-500">Chargement du dossier...</div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 mb-4">{error || 'Dossier introuvable'}</p>
          <button onClick={() => navigate('/investigation/search')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Retour à la recherche</button>
        </div>
      </div>
    );
  }

  const { guest, stay_history, hotels_visited, companions, alerts, identity_matches, stats } = data;

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <button onClick={() => navigate(-1)} className="text-blue-600 hover:text-blue-800 text-sm mb-4 inline-block">
        ← Retour
      </button>

      {/* Guest Identity Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">👤 {guest.full_name}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 text-sm">
          <div>
            <span className="text-gray-500 block">Date de naissance</span>
            <span className="font-medium">{guest.date_of_birth ? new Date(guest.date_of_birth).toLocaleDateString() : '-'}</span>
          </div>
          <div>
            <span className="text-gray-500 block">Nationalité</span>
            <span className="font-medium">{guest.nationality}</span>
          </div>
          <div>
            <span className="text-gray-500 block">Document</span>
            <span className="font-medium">{guest.document_type} — {guest.document_number}</span>
          </div>
          <div>
            <span className="text-gray-500 block">Téléphone</span>
            <span className="font-medium">{guest.phone_number || '-'}</span>
          </div>
          <div>
            <span className="text-gray-500 block">ID</span>
            <span className="font-mono text-xs">{guest.id}</span>
          </div>
        </div>
      </div>

      {/* Statistics Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {[
          { label: 'Séjours', value: stats.total_stays, color: 'blue' },
          { label: 'Hôtels', value: stats.total_hotels, color: 'indigo' },
          { label: 'Nuits', value: stats.total_nights, color: 'purple' },
          { label: 'Accompagnants', value: stats.total_companions, color: 'teal' },
          { label: 'Alertes actives', value: stats.active_alerts, color: 'red' },
          { label: 'Correspondances', value: stats.identity_matches, color: 'orange' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-lg shadow p-4 text-center">
            <div className="text-xs text-gray-500">{s.label}</div>
            <div className={`text-2xl font-bold text-${s.color}-600`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Stay History */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">📋 Historique des séjours</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hôtel</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Province / Ville</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-in</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-out</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nuits</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chambre</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(!stay_history || stay_history.length === 0) ? (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Aucun séjour enregistré</td></tr>
              ) : stay_history.map((stay, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{stay.hotel_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{stay.hotel_province} / {stay.hotel_city}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{new Date(stay.check_in_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{stay.check_out_date ? new Date(stay.check_out_date).toLocaleDateString() : '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{stay.nights_count}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{stay.room_number || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      stay.has_alerts ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                    }`}>{stay.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hotels Visited */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">🏨 Hôtels visités</h3>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(!hotels_visited || hotels_visited.length === 0) ? (
            <p className="text-gray-400 col-span-full">Aucun hôtel</p>
          ) : hotels_visited.map((h) => (
            <div key={h.hotel_id} className="border rounded-lg p-4">
              <div className="font-medium text-gray-900">{h.hotel_name}</div>
              <div className="text-sm text-gray-500">{h.province} / {h.city}</div>
              <div className="mt-2 text-sm grid grid-cols-2 gap-1">
                <span>Visites: <strong>{h.total_visits}</strong></span>
                <span>Nuits: <strong>{h.total_nights}</strong></span>
                <span>Première: {new Date(h.first_visit).toLocaleDateString()}</span>
                <span>Dernière: {new Date(h.last_visit).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Companions */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">👥 Accompagnants</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Relation</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Séjours partagés</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Première fois</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dernière fois</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(!companions || companions.length === 0) ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Aucun accompagnant</td></tr>
              ) : companions.map((c) => (
                <tr key={c.person_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{c.full_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{c.relationship || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{c.shared_stays}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{new Date(c.first_seen).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{new Date(c.last_seen).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Active Alerts */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">🚨 Alertes actives</h3>
        </div>
        <div className="p-6">
          {(!alerts || alerts.length === 0) ? (
            <p className="text-gray-400">Aucune alerte</p>
          ) : (
            <div className="space-y-3">
              {alerts.map((a) => (
                <div key={a.id} className={`border-l-4 p-3 rounded ${
                  a.severity === 'HIGH' || a.severity === 'CRITICAL' ? 'border-red-500 bg-red-50' :
                  a.severity === 'MEDIUM' ? 'border-yellow-500 bg-yellow-50' : 'border-blue-500 bg-blue-50'
                }`}>
                  <div className="flex justify-between">
                    <span className="font-medium text-sm">{a.type}</span>
                    <span className="text-xs text-gray-500">{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{a.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Identity Matches */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">🔗 Correspondances d'identité</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confiance</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Personne liée</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confirmé par</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(!identity_matches || identity_matches.length === 0) ? (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">Aucune correspondance</td></tr>
              ) : identity_matches.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{m.match_type}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      m.confidence > 0.9 ? 'bg-red-100 text-red-700' :
                      m.confidence > 0.7 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                    }`}>{(m.confidence * 100).toFixed(1)}%</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono text-xs">
                    {m.person_id_a === id ? m.person_id_b : m.person_id_a}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {m.confirmed_by ? `${m.confirmed_by} — ${m.confirmed_at ? new Date(m.confirmed_at).toLocaleDateString() : ''}` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default InvestigationPersonDetailPage;
